import { Renderer2 } from '@angular/core';

export interface TableColumnSettings {
  columnSizes?: { [key: string]: number };
  columnOrder?: string[];
  [key: string]: any;
}

const MIN_COLUMN_WIDTH = 40;
const AUTO_SCROLL_SPEED = 15;
const AUTO_SCROLL_INTERVAL = 20;
const AUTO_SCROLL_EDGE_THRESHOLD = 50;

export class TableColumnManager<T extends TableColumnSettings> {
  private el!: HTMLElement;
  private headerElements: HTMLElement[] = [];
  private styleEl: HTMLStyleElement | null = null;
  private columns: string[] = [];
  private unlistenFuncs: (() => void)[] = [];
  private activeOnMouseMove: ((e: MouseEvent) => void) | null = null;
  private activeOnMouseUp: (() => void) | null = null;
  private dragMouseMoveUnlisten: (() => void) | null = null;
  private dragMouseUpUnlisten: (() => void) | null = null;
  private animationFrameId: number | null = null;
  private scrollInterval: ReturnType<typeof setInterval> | null = null;
  private isDraggingColumn: boolean = false;
  private saveSettingsTimeout: any = null;

  constructor(
    public uniqueId: string,
    public defaultWidths: { [key: string]: number },
    public settings: T,
    public onSettingsChange: (updatedSettings: T) => void,
    private renderer: Renderer2
  ) {}

  public init(headerContainer: HTMLElement): void {
    this.el = headerContainer;

    const headerCells = Array.from(headerContainer.querySelectorAll('.th, .td')) as HTMLElement[];
    const discovered: string[] = [];

    this.headerElements = headerCells;
    headerCells.forEach((cell) => {
      const colId = this.getColumnIdFromElement(cell);
      if (colId) {
        discovered.push(colId);
      }
    });
    this.columns = discovered;

    const middleColumnIds = discovered.filter(
      (id) => !id.includes('sticky') && id !== 'col-id' && id !== 'col-name' && id !== 'col-actions'
    );

    if (!this.settings) {
      this.settings = {} as T;
    }

    if (!this.settings.columnSizes) {
      this.settings.columnSizes = {};
    }

    const domSet = new Set(middleColumnIds);
    const loadedSet = new Set(this.settings.columnOrder || []);

    let isCorrupted = false;
    if (domSet.size !== loadedSet.size) {
      isCorrupted = true;
    } else {
      for (const id of middleColumnIds) {
        if (!loadedSet.has(id)) {
          isCorrupted = true;
          break;
        }
      }
    }

    if (isCorrupted || !this.settings.columnOrder || this.settings.columnOrder.length === 0) {
      const mergedOrder = (this.settings.columnOrder || [])
        .filter(id => domSet.has(id));

      middleColumnIds.forEach((id) => {
        if (!mergedOrder.includes(id)) {
          const defaultIndex = middleColumnIds.indexOf(id);
          if (defaultIndex >= 0 && defaultIndex <= mergedOrder.length) {
            mergedOrder.splice(defaultIndex, 0, id);
          } else {
            mergedOrder.push(id);
          }
        }
      });

      this.settings.columnOrder = mergedOrder;
      this.saveSettings(true);
    }

    this.setupColumns();
    this.updateStyle();
    this.addTrackedListener(headerContainer, 'dragover', (event: DragEvent) => {
      this.checkAutoScroll(event.clientX);
    });
  }

  public updateSettings(newSettings: T): void {
    if (newSettings) {
      this.settings = newSettings;
      if (!this.settings.columnSizes) {
        this.settings.columnSizes = {};
      }
      this.updateStyle();
    }
  }

  public destroy(): void {
    // Cancel any pending animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.saveSettingsTimeout) {
      clearTimeout(this.saveSettingsTimeout);
      this.saveSettingsTimeout = null;
    }

    // Remove resize handles from elements
    this.headerElements.forEach((th) => {
      if (th) {
        const handle = th.querySelector('.column-resize-handle');
        if (handle) {
          const parent = this.renderer.parentNode(handle);
          if (parent) {
            this.renderer.removeChild(parent, handle);
          }
        }
      }
    });
    this.headerElements = [];
    this.columns = [];

    // 1. Remove style element programmatically using Renderer2
    if (this.styleEl) {
      const parent = this.renderer.parentNode(this.styleEl);
      if (parent) {
        this.renderer.removeChild(parent, this.styleEl);
      }
      this.styleEl = null;
    }

    // 2. Remove all tracked event listeners by calling their unlisten callbacks
    this.unlistenFuncs.forEach((unlisten) => unlisten());
    this.unlistenFuncs = [];

    // 3. Remove active drag-resize and auto-scroll timers
    this.cleanupActiveDrag();
    this.stopAutoScroll();
    this.isDraggingColumn = false;
  }

  public reinit(headerContainer: HTMLElement): void {
    this.destroy();
    this.init(headerContainer);
  }

  private addTrackedListener(element: any, type: string, handler: (event: any) => void): void {
    const unlisten = this.renderer.listen(element, type, handler);
    this.unlistenFuncs.push(unlisten);
  }

  private cleanupActiveDrag(): void {
    if (this.dragMouseMoveUnlisten) {
      this.dragMouseMoveUnlisten();
      this.dragMouseMoveUnlisten = null;
    }
    if (this.dragMouseUpUnlisten) {
      this.dragMouseUpUnlisten();
      this.dragMouseUpUnlisten = null;
    }
    this.activeOnMouseMove = null;
    this.activeOnMouseUp = null;
  }

  private checkAutoScroll(clientX: number): void {
    if (!this.isDraggingColumn) {
      this.stopAutoScroll();
      return;
    }

    const rect = this.el.getBoundingClientRect();
    const rightThreshold = rect.right - AUTO_SCROLL_EDGE_THRESHOLD;

    // Set left threshold to be immediately to the right of the left sticky column
    const leftStickyCol = this.el.querySelector('.sticky-col:not(.sticky-col-right)') as HTMLElement;
    let leftThreshold = rect.left + AUTO_SCROLL_EDGE_THRESHOLD;
    let stickyRight = rect.left;

    if (leftStickyCol) {
      const stickyRect = leftStickyCol.getBoundingClientRect();
      stickyRight = stickyRect.right;
      leftThreshold = stickyRight + AUTO_SCROLL_EDGE_THRESHOLD;
    }

    const bodyContainer = this.el.parentElement?.querySelector('.body-container') as HTMLElement;
    if (!bodyContainer) return;

    if (clientX < leftThreshold && clientX > stickyRight) {
      if (!this.scrollInterval) {
        this.scrollInterval = setInterval(() => {
          bodyContainer.scrollLeft -= AUTO_SCROLL_SPEED;
        }, AUTO_SCROLL_INTERVAL);
      }
    } else if (clientX > rightThreshold) {
      if (!this.scrollInterval) {
        this.scrollInterval = setInterval(() => {
          bodyContainer.scrollLeft += AUTO_SCROLL_SPEED;
        }, AUTO_SCROLL_INTERVAL);
      }
    } else {
      this.stopAutoScroll();
    }
  }

  private stopAutoScroll(): void {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  private getColumnIdFromElement(el: HTMLElement): string | null {
    let matched: string | null = null;
    const colRegex = /^col-(?!xs|sm|md|lg|xl|xxl|\d)(.+)$/;
    for (let i = 0; i < el.classList.length; i++) {
      const cls = el.classList[i];
      if (colRegex.test(cls)) {
        if (cls === 'col-address' || cls === 'col-load' || cls === 'col-capacity') {
          if (!matched) matched = cls;
        } else {
          matched = cls;
        }
      }
    }
    return matched;
  }

  private setupColumns(): void {
    this.headerElements.forEach((th) => {
      const colId = this.getColumnIdFromElement(th);
      if (!colId) return;

      const isActions = th.classList.contains('col-actions') || colId === 'col-actions' || colId.includes('col-actions');
      const isSticky = th.classList.contains('sticky-col') || colId === 'col-id' || colId === 'col-name';

      if (!isActions) {
        // 1. Setup Resize Handle
        this.addResizeHandle(th, colId);
      }

      if (!isSticky && !isActions) {
        // 2. Setup Drag and Drop
        this.addDragEvents(th, colId);
      }
    });
  }

  private addResizeHandle(th: HTMLElement, colId: string): void {
    if (th.querySelector('.column-resize-handle')) return;

    const handle = this.renderer.createElement('div');
    this.renderer.addClass(handle, 'column-resize-handle');
    const isSticky = th.classList.contains('sticky-col') || colId === 'col-id' || colId === 'col-name';
    if (!isSticky) {
      this.renderer.setStyle(th, 'position', 'relative');
    }
    this.renderer.appendChild(th, handle);

    const onMouseDown = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      // Clean up previous listeners if any exist
      this.cleanupActiveDrag();

      const startX = event.clientX;
      const startWidth = th.getBoundingClientRect().width;

      this.activeOnMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + deltaX);
        if (this.settings.columnSizes) {
          this.settings.columnSizes[colId] = newWidth;
        }

        // requestAnimationFrame throttled update to eliminate layout thrashing
        if (this.animationFrameId === null) {
          this.animationFrameId = requestAnimationFrame(() => {
            this.updateStyle();
            this.animationFrameId = null;
          });
        }
      };

      this.activeOnMouseUp = () => {
        this.cleanupActiveDrag();
        this.saveSettings();
      };

      this.dragMouseMoveUnlisten = this.renderer.listen('document', 'mousemove', this.activeOnMouseMove);
      this.dragMouseUpUnlisten = this.renderer.listen('document', 'mouseup', this.activeOnMouseUp);
    };

    this.addTrackedListener(handle, 'mousedown', onMouseDown);
  }

  private addDragEvents(th: HTMLElement, colId: string): void {
    this.renderer.setAttribute(th, 'draggable', 'true');

    this.addTrackedListener(th, 'dragstart', (event: DragEvent) => {
      event.dataTransfer?.setData('text/plain', colId);
      this.renderer.addClass(th, 'column-dragging');
      this.isDraggingColumn = true;
    });

    this.addTrackedListener(th, 'dragend', () => {
      this.renderer.removeClass(th, 'column-dragging');
      this.headerElements.forEach((header) => this.renderer.removeClass(header, 'column-drag-over'));
      this.isDraggingColumn = false;
      this.stopAutoScroll();
    });

    this.addTrackedListener(th, 'dragover', (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      this.renderer.addClass(th, 'column-drag-over');
    });

    this.addTrackedListener(th, 'dragleave', () => {
      this.renderer.removeClass(th, 'column-drag-over');
    });

    this.addTrackedListener(th, 'drop', (event: DragEvent) => {
      event.preventDefault();
      this.renderer.removeClass(th, 'column-drag-over');
      this.isDraggingColumn = false;
      this.stopAutoScroll();
      const draggedColId = event.dataTransfer?.getData('text/plain');
      if (draggedColId && draggedColId !== colId) {
        this.reorderColumns(draggedColId, colId);
      }
    });
  }

  private reorderColumns(dragged: string, target: string): void {
    const order = this.settings.columnOrder || [];
    const dragIdx = order.indexOf(dragged);
    const targetIdx = order.indexOf(target);
    if (dragIdx === -1 || targetIdx === -1) return;

    const newOrder = [...order];
    if (Math.abs(dragIdx - targetIdx) === 1) {
      newOrder[dragIdx] = target;
      newOrder[targetIdx] = dragged;
    } else {
      newOrder.splice(dragIdx, 1);
      const newTargetIdx = newOrder.indexOf(target);
      newOrder.splice(newTargetIdx, 0, dragged);
    }

    this.settings.columnOrder = newOrder;
    this.updateStyle();
    this.saveSettings();
  }

  private updateStyle(): void {
    if (!this.styleEl) {
      this.styleEl = this.renderer.createElement('style');
      this.renderer.setAttribute(this.styleEl, 'id', `style-${this.uniqueId}`);
      this.renderer.appendChild(document.head, this.styleEl);
    }

    const sizes = this.settings.columnSizes || {};
    const order = this.settings.columnOrder || [];
    const widthOf = (colId: string) => sizes[colId] || this.defaultWidths[colId] || 100;

    // Both col-id and col-name (if both are present) pin left; multiple columns can pin right
    // via col-actions. Stack them by their position among this.columns (DOM order) instead of
    // hardcoding left/right to 0 for all of them, so a second pinned column - e.g. a selection
    // checkbox using col-id next to a col-name column - doesn't render on top of the first one.
    const pinnedLeftIds = this.columns.filter((id) => id === 'col-id' || id === 'col-name');
    const pinnedRightIds = this.columns.filter((id) => id === 'col-actions' || id.includes('col-actions'));

    const leftOffsets: { [key: string]: number } = {};
    pinnedLeftIds.reduce((offset, id) => {
      leftOffsets[id] = offset;
      return offset + widthOf(id);
    }, 0);

    const rightOffsets: { [key: string]: number } = {};
    [...pinnedRightIds].reverse().reduce((offset, id) => {
      rightOffsets[id] = offset;
      return offset + widthOf(id);
    }, 0);

    let css = '';
    this.columns.forEach((colId) => {
      const width = widthOf(colId);
      let orderStr = '';

      if (colId in leftOffsets) {
        orderStr = `
          order: -100 !important;
          position: sticky !important;
          left: ${leftOffsets[colId]}px !important;
          z-index: 6 !important;
        `;
      } else if (colId in rightOffsets) {
        orderStr = `
          order: 1000 !important;
          position: sticky !important;
          right: ${rightOffsets[colId]}px !important;
          left: auto !important;
          z-index: 6 !important;
        `;
      } else {
        const orderIdx = order.indexOf(colId);
        if (orderIdx !== -1) {
          orderStr = `order: ${orderIdx} !important;`;
        }
      }

      css += `
        .table-outer-wrapper.table-${this.uniqueId} .${colId} {
          width: ${width}px !important;
          flex: 0 0 ${width}px !important;
          ${orderStr}
        }
      `;
    });

    this.renderer.setProperty(this.styleEl, 'textContent', css);
  }

  private saveSettings(immediate: boolean = false): void {
    if (this.saveSettingsTimeout) {
      clearTimeout(this.saveSettingsTimeout);
      this.saveSettingsTimeout = null;
    }

    const performSave = () => {
      this.onSettingsChange({
        ...this.settings,
        columnSizes: { ...this.settings.columnSizes },
        columnOrder: [...(this.settings.columnOrder || [])]
      });
    };

    if (immediate) {
      performSave();
    } else {
      this.saveSettingsTimeout = setTimeout(() => {
        performSave();
        this.saveSettingsTimeout = null;
      }, 300); // 300ms debounce
    }
  }
}
